import { useState, useCallback } from 'react';
import apiService from '../../services/api';
import { ServiceType } from '../../services/api';
import { StockInstrument } from '../../types';

const coreService = apiService.getService(ServiceType.CORE);

interface UsePairConfigLoaderProps {
    setTakeProfitReturn: (value: number) => void;
    setCutLossReturn: (value: number) => void;
    setIsRunning: (value: boolean) => void;
    setMaxOpen: (value: number) => void;
    setSelectedStocks: React.Dispatch<React.SetStateAction<StockInstrument[]>>;
    setStockProportion: (value: number) => void;
    // v3 reference-PnL trigger setters.
    setActiveSide?: (value: 'BUY' | 'SELL') => void;
    setFutureInstrumentPrice?: (value: number | null) => void;
    setIsPause?: (value: boolean) => void;
    setOpenMode?: (value: 'IMMEDIATE' | 'REFERENCE') => void;
    setMaxHoldingDays?: (value: number) => void;
    // Optional dual-leg setters — pages that don't show the allocator can omit.
    setDerivPortfolioId?: (value: string | undefined) => void;
    setStockAllocatedCash?: (value: number | null) => void;
    setDerivAllocatedCash?: (value: number | null) => void;
    setBalanceMode?: (value: 'NOTIONAL_MATCH' | 'MANUAL') => void;
}

export const usePairConfigLoader = ({
    setTakeProfitReturn,
    setCutLossReturn,
    setIsRunning,
    setMaxOpen,
    setSelectedStocks,
    setStockProportion,
    setActiveSide,
    setFutureInstrumentPrice,
    setIsPause,
    setOpenMode,
    setMaxHoldingDays,
    setDerivPortfolioId,
    setStockAllocatedCash,
    setDerivAllocatedCash,
    setBalanceMode,
}: UsePairConfigLoaderProps) => {
    const [loading, setLoading] = useState(false);

    // `isCurrent`: a superseded load (the user switched pair mid-fetch) must not
    // write the stocks it fetched over the newer pair's.
    const loadPairConfigIntoForm = useCallback(async (config: any, isCurrent?: () => boolean) => {
        if (!config) return;


        setLoading(true);

        try {
            // Check if config is nested in a settings object
            const settings = config.settings || config;
            
            // close_type is not loaded into the form any more: RETURN is the only
            // mode the user can pick, and closing on demand goes through Flatten.
            if (settings.take_profit_return !== undefined && settings.take_profit_return !== null) {
                const value = settings.take_profit_return; // Convert from decimal to percentage
                setTakeProfitReturn(value);
            }
            if (settings.cut_loss_return !== undefined && settings.cut_loss_return !== null) {
                const value = settings.cut_loss_return; // Convert from decimal to percentage
                setCutLossReturn(value);
            }
            if (settings.is_running !== undefined && settings.is_running !== null) {
                setIsRunning(settings.is_running);
            }
            if (settings.max_open !== undefined && settings.max_open !== null) {
                setMaxOpen(settings.max_open);
            }
            // v3 reference-PnL trigger fields.
            if (setActiveSide && (settings.active_side === 'BUY' || settings.active_side === 'SELL')) {
                setActiveSide(settings.active_side);
            }
            if (setFutureInstrumentPrice && settings.future_instrument_price !== undefined) {
                setFutureInstrumentPrice(settings.future_instrument_price ?? null);
            }
            if (setIsPause && settings.is_pause !== undefined && settings.is_pause !== null) {
                setIsPause(Boolean(settings.is_pause));
            }
            // A saved config without the key predates the mode: it was set up for the
            // reference trigger and keeps it.
            if (setOpenMode) {
                setOpenMode(settings.open_mode === 'IMMEDIATE' ? 'IMMEDIATE' : 'REFERENCE');
            }
            if (setMaxHoldingDays && settings.max_holding_days !== undefined && settings.max_holding_days !== null) {
                setMaxHoldingDays(Number(settings.max_holding_days));
            }

            // Dual-leg allocator knobs (optional — only restored if the page
            // wired a setter in. Skipped silently otherwise.)
            if (setDerivPortfolioId && settings.deriv_portfolio_id !== undefined) {
                setDerivPortfolioId(settings.deriv_portfolio_id ?? undefined);
            }
            if (setStockAllocatedCash && settings.stock_allocated_cash !== undefined) {
                setStockAllocatedCash(settings.stock_allocated_cash ?? null);
            }
            if (setDerivAllocatedCash && settings.deriv_allocated_cash !== undefined) {
                setDerivAllocatedCash(settings.deriv_allocated_cash ?? null);
            }
            if (setBalanceMode && (settings.balance_mode === 'NOTIONAL_MATCH' || settings.balance_mode === 'MANUAL')) {
                setBalanceMode(settings.balance_mode);
            }

            // Load stock proportions and refetch stocks
            const stockProps = settings.stock_proportion || config.stock_proportion;
            if (stockProps) {
                const token = localStorage.getItem('access_token');
                
                // Get list of stock symbols from config
                const configStockSymbols = Object.keys(stockProps);
                
                // Refetch all stocks from config
                if (configStockSymbols.length > 0 && token) {
                    try {
                        // One request per configured stock, issued in PARALLEL. This
                        // used to be a sequential await inside a for-loop, so the
                        // form's load time was the SUM of every round trip — with a
                        // handful of stocks that is seconds of visible "loading".
                        // Order is preserved because Promise.all resolves positionally.
                        const fetchedStocks = (
                            await Promise.all(
                                configStockSymbols.map(async (formattedSymbol): Promise<StockInstrument | null> => {
                                    try {
                                        // Extract symbol after colon if present for search
                                        const symbolToSearch = formattedSymbol.includes(':')
                                            ? formattedSymbol.split(':')[1]
                                            : formattedSymbol;

                                        const response = await coreService.get(`/search/stocks?q=${encodeURIComponent(symbolToSearch)}`);

                                        if (!response.data.results || response.data.results.length === 0) {
                                            return null;
                                        }
                                        // Get the first result
                                        const stockData = response.data.results[0];

                                        // v3 value is the tuple [ratio, ref_price,
                                        // flash_volume_limit]; legacy is a bare ratio.
                                        const raw = stockProps[formattedSymbol];
                                        const ratio = Array.isArray(raw) ? (raw[0] ?? 0) : (raw || 0);
                                        const refPrice = Array.isArray(raw) ? (raw[1] ?? null) : null;
                                        const flashLimit = Array.isArray(raw) ? (raw[2] ?? null) : null;

                                        return {
                                            symbol: stockData.symbol,
                                            name: stockData.symbol,
                                            price: stockData.price,
                                            volume: 0,
                                            marketCap: 0,
                                            change24h: stockData.latestChange || 0,
                                            changePercent24h: stockData.changePercent || 0,
                                            proportion: ratio,
                                            referencePrice: refPrice,
                                            flashVolumeLimit: flashLimit,
                                        };
                                    } catch (error) {
                                        // One bad symbol must not drop the rest of the basket.
                                        console.error(`Error fetching stock ${formattedSymbol}:`, error);
                                        return null;
                                    }
                                }),
                            )
                        ).filter((s): s is StockInstrument => s !== null);

                        if (isCurrent && !isCurrent()) return;
                        // Replace all selected stocks with fetched stocks
                        if (fetchedStocks.length > 0) {
                            setSelectedStocks(fetchedStocks);
                        }
                    } catch (error) {
                        console.error('Error fetching stocks from config:', error);
                    }
                }
            }

            // Calculate total stock proportion
            const stockProportionObj = settings.stock_proportion || config.stock_proportion;
            if (stockProportionObj && typeof stockProportionObj === 'object') {
                const totalStockProp = Object.values(stockProportionObj).reduce(
                    (sum: number, val: any) => sum + (Array.isArray(val) ? (val[0] || 0) : (val || 0)),
                    0,
                );
                setStockProportion(totalStockProp);
            }
        } catch (error) {
            console.error('Error loading pair configuration:', error);
        } finally {
            setLoading(false);
        }
    }, [
        setTakeProfitReturn,
        setCutLossReturn,
        setIsRunning,
        setMaxOpen,
            setSelectedStocks,
        setStockProportion,
        setActiveSide,
        setFutureInstrumentPrice,
        setIsPause,
        setOpenMode,
        setMaxHoldingDays,
        setDerivPortfolioId,
        setStockAllocatedCash,
        setDerivAllocatedCash,
        setBalanceMode,
    ]);

    return {
        loadPairConfigIntoForm,
        loading
    };
};

